import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { SiteOpeningHoursForm } from '../../../../interfaces/customer-sites';
import {
  IBaseSiteOpeningHoursDisplay,
  ITaskSiteOpeningHoursDisplay,
  SiteOpeningHoursService,
} from '../../../../services/site-opening-hours.service';
import { TaskService } from '../../../../services';
import { JobScreenSiteOpeningTask } from '../../../../interfaces/screen-specific/job-screen';
import { JobScreenCommonService } from '../job-screen-common.service';
import { addDays } from 'date-fns';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';

@Component({
  selector: 'job-screen-site-opening',
  templateUrl: './job-screen-site-opening.component.html',
  styleUrls: ['./job-screen-site-opening.component.scss'],
})
export class JobScreenSiteOpeningComponent implements OnChanges {
  public editVisible: boolean = false;
  public pickupSiteHoursFormGroup: FormArray<FormGroup<SiteOpeningHoursForm>>;
  public dropOffSiteHoursFormGroup: FormArray<FormGroup<SiteOpeningHoursForm>>;

  taskSiteOpeningHours: ITaskSiteOpeningHoursDisplay;
  public task: JobScreenSiteOpeningTask;

  // Get the current day of the week
  public currentDay = new Date().getDay();
  public daysOfWeek = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  public today: string;
  public tommorow: string;

  @Input() taskId: number;
  @Input() organisationTypeId: number;
  onlyTodayAndTomorrow = { pickup: true, dropOff: true };
  loadingTask: boolean;

  protected readonly OrganisationTypeEnum = OrganisationTypeEnum;

  constructor(
    private taskService: TaskService,
    private commonService: JobScreenCommonService,
    private siteOpeningHoursService: SiteOpeningHoursService,
  ) {
    this.pickupSiteHoursFormGroup = this.siteOpeningHoursService.createForm([], true);
    this.dropOffSiteHoursFormGroup = this.siteOpeningHoursService.createForm([], true);

    this.today = this.daysOfWeek[this.currentDay];
    this.tommorow = this.daysOfWeek[this.currentDay + 1];
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.taskId) {
      this.fetchTask(this.taskId).then(() => {});
      this.fetchHours(this.taskId).then(() => {});
    }
  }

  editHours() {
    this.siteOpeningHoursService.patchFormFromDisplay(
      this.pickupSiteHoursFormGroup,
      this.taskSiteOpeningHours.pickup,
    );
    this.siteOpeningHoursService.patchFormFromDisplay(
      this.dropOffSiteHoursFormGroup,
      this.taskSiteOpeningHours.dropOff,
    );
    this.editVisible = true;
  }

  editAllowed() {
    return (
      !this.commonService.isJobCompleted(this.task?.order?.job) && this.organisationTypeId != 3
    );
  }

  async saveOpeniningHours() {
    this.siteOpeningHoursService
      .updateTaskSiteOpeningHours(this.taskId, {
        pickup: this.siteOpeningHoursService.getOpeningHours(this.pickupSiteHoursFormGroup),
        dropOff: this.siteOpeningHoursService.getOpeningHours(this.dropOffSiteHoursFormGroup),
      })
      .then(() => {
        this.editVisible = false;
        this.fetchHours(this.taskId).then(() => {});
      });
  }

  async fetchHours(taskId: number) {
    this.siteOpeningHoursService.getTaskSiteOpeningHoursDisplay(taskId).then((openingHours) => {
      this.taskSiteOpeningHours = openingHours;
    });
  }

  async fetchTask(taskId: number) {
    this.loadingTask = true;
    this.taskService
      .getForComponent(taskId, 'job-screen-site-opening')
      .then((task: JobScreenSiteOpeningTask) => {
        this.task = task;
        this.loadingTask = false;
      });
  }

  filterDates(dates: IBaseSiteOpeningHoursDisplay[], toggleType: string) {
    return dates.filter(
      (date) =>
        !this.onlyTodayAndTomorrow[toggleType] ||
        date.dayOfWeek === this.today ||
        date.dayOfWeek === this.tommorow,
    );
  }

  dayOfWeekName(dayOfWeek: string, toggleType: string) {
    return !this.onlyTodayAndTomorrow[toggleType]
      ? dayOfWeek.substring(0, 3)
      : dayOfWeek === this.today
      ? 'Today'
      : 'Tomorrow';
  }

  public get pickupAddressString(): string {
    if (!this.task) {
      return '';
    }

    const fields = [
      this.task.pickupLocation.businessName,
      this.task.pickupLocation.city,
      this.task.pickupLocation.postcode,
    ];

    return fields
      .filter((x) => x && x != '')
      .map((x) => x.trim())
      .join(', ');
  }

  public get dropOffAddressString(): string {
    if (!this.task) {
      return '';
    }

    const fields = [
      this.task.dropOffLocation.businessName,
      this.task.dropOffLocation.city,
      this.task.dropOffLocation.postcode,
    ];

    return fields
      .filter((x) => x && x != '')
      .map((x) => x.trim())
      .join(', ');
  }

  asSiteHours(hoursUntyped: any): IBaseSiteOpeningHoursDisplay {
    return hoursUntyped as IBaseSiteOpeningHoursDisplay;
  }
}
